import React, { useCallback, useEffect, useState } from 'react';
import Collaborator from 'Components/Collaborator/Collaborator';
import FormikTextField from 'Components/Form/FormikTextField';
import { EMAIL_REGEX } from 'constants';
import { Formik } from 'formik';
import {
  copyToClipboard,
  getAccessDescription,
  getNameInitials,
} from 'helpers';
import { getRequest, postRequest } from 'helpers/axios';
import Modal from 'react-bootstrap/Modal';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import ModalStyles from './ShareModalNew.module.scss';
import { FiLink, FiMail, FiCopy, FiRefreshCw } from 'react-icons/fi';
import { getAccessLevels } from 'services/userService';
import { togglePublicSharing, regeneratePublicLink } from 'services/documentService';

const getAccessLabel = (name) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <p style={{
        margin: 0,
        fontSize: '14px',
        fontWeight: '600',
        fontFamily: "'Inter', sans-serif",
        color: '#101828',
        lineHeight: '20px'
      }}>
        {name}
      </p>
      <span style={{
        fontSize: '12px',
        fontWeight: '400',
        fontFamily: "'Inter', sans-serif",
        color: '#667085',
        lineHeight: '16px'
      }}>
        {getAccessDescription(name)}
      </span>
    </div>
  );
};

const Styles = {
  option: (styles, { isSelected, isFocused }) => {
    return {
      padding: '10px 14px',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: "'Inter', sans-serif",
      color: isSelected ? '#00a7b7' : '#344054',
      backgroundColor: isFocused ? '#F9FAFB' : '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      borderLeft: isSelected ? '3px solid #00a7b7' : '3px solid transparent',

      '&:active': {
        backgroundColor: '#F2F4F7',
      },
    };
  },

  control: (baseStyles, { isFocused }) => {
    return {
      ...baseStyles,
      minHeight: '40px',
      borderRadius: '6px',
      border: `1px solid ${isFocused ? '#00a7b7' : '#D0D5DD'}`,
      boxShadow: isFocused ? '0 0 0 3px rgba(0, 167, 183, 0.1)' : 'none',
      cursor: 'pointer',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      transition: 'all 0.2s ease',

      '&:hover': {
        borderColor: '#00a7b7',
      },
    };
  },

  menu: (baseStyles) => {
    return {
      ...baseStyles,
      borderRadius: '8px',
      boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      border: '1px solid #E4E7EC',
      overflow: 'hidden',
      marginTop: '4px',
    };
  },

  menuList: (baseStyles) => {
    return {
      ...baseStyles,
      padding: '4px',
    };
  },

  singleValue: (baseStyles) => {
    return {
      ...baseStyles,
      color: '#344054',
      fontWeight: '500',
      fontFamily: "'Inter', sans-serif",
    };
  },

  placeholder: (baseStyles) => {
    return {
      ...baseStyles,
      color: '#98A2B3',
      fontFamily: "'Inter', sans-serif",
    };
  },

  dropdownIndicator: (baseStyles, { isFocused }) => {
    return {
      ...baseStyles,
      color: isFocused ? '#00a7b7' : '#667085',
      transition: 'all 0.2s ease',

      '&:hover': {
        color: '#00a7b7',
      },
    };
  },

  indicatorSeparator: () => {
    return {
      display: 'none',
    };
  },
};

const initialValues = {
  email: '',
  shareType: '',
};

const validationSchema = Yup.object({
  email: Yup.string().matches(EMAIL_REGEX, 'Invalid email').required(),
  shareType: Yup.string().required(),
});

const ShareModal = ({
  template_id,
  document,
  isDocument = true,
  folder,
  fetchFolders,
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [pendingCollaborators, setPendingCollaborators] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]); // Local pending invites before sharing
  const [documentOwner, setDocumentOwner] = useState({});
  const [publicSharingEnabled, setPublicSharingEnabled] = useState(false);
  const [publicToken, setPublicToken] = useState('');
  const [isPublicLinkCopied, setIsPublicLinkCopied] = useState(false);
  const [isLoadingPublicShare, setIsLoadingPublicShare] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const getPublicUrl = (token) => {
    return token ? `${window.location.origin}/public/${token}` : '';
  };

  const { documentId: documentIdParams } = useParams();
  const { id: documentIdProps } = document || {};
  const { id: folderId } = folder || {};

  const documentId = documentIdProps || documentIdParams;

  const shareStr = isDocument ? 'documents' : 'folders';

  const fetchAccessLevels = useCallback(async () => {
    try {
      const accessLevels = await getAccessLevels();
      const { data } = accessLevels || {};
      const { access_levels } = data || {};
      const transformedAccessLevels = access_levels?.map((accessLevel) => {
        return {
          value: accessLevel.level,
          id: accessLevel.id,
          label: getAccessLabel(accessLevel.name),
          uuid: accessLevel.uuid,
        };
      });

      setOptions(transformedAccessLevels || []);
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchAccessLevels();
  }, [fetchAccessLevels]);

  const fetchCollaborators = useCallback(async () => {
    try {
      const collaboratorResponse = await getRequest(
        `${shareStr}/${isDocument ? documentId : folder.id}/collaborators`,
      );

      const { data } = collaboratorResponse || {};
      const { shared_documents, pending_collaborators, shared_folders } =
        data || {};

      setCollaborators(shared_documents || shared_folders || []);
      setPendingCollaborators(pending_collaborators || []);
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchCollaborators();
  }, [fetchCollaborators]);

  const fetchDocumentOwner = useCallback(async () => {
    try {
      const documentReponse = await getRequest(
        `${shareStr}/${isDocument ? documentId : folder.id}`,
      );
      const { data } = documentReponse || {};
      const { document, folder: dataFolder } = data || {};
      const { user: userDocument } = document || {};
      const { user: userFolder } = dataFolder || {};
      setDocumentOwner(userDocument || userFolder || {});
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchDocumentOwner();
  }, [fetchDocumentOwner]);

  useEffect(() => {
    if (document) {
      setPublicSharingEnabled(document.public_sharing_enabled || false);
      setPublicToken(document.public_token || '');
    }
  }, [document]);

  useEffect(() => {
    if (isPublicLinkCopied) {
      setTimeout(() => setIsPublicLinkCopied(false), 3000);
    }
  }, [isPublicLinkCopied]);

  const handlePublicSharingToggle = async (enabled) => {
    if (!isDocument) return; // Only for documents

    setIsLoadingPublicShare(true);
    try {
      const response = await togglePublicSharing(documentId, enabled);
      const { data } = response || {};
      setPublicSharingEnabled(data.public_sharing_enabled);
      setPublicToken(data.public_token || '');

      // Show message based on the action we're performing
      if (enabled) {
        // Auto-copy link when enabled
        if (data.public_token) {
          const publicUrl = getPublicUrl(data.public_token);
          copyToClipboard(publicUrl);
          setIsPublicLinkCopied(true);
          toast.success('Public sharing enabled and link copied!');
        } else {
          toast.success('Public sharing enabled!');
        }
      } else {
        toast.success('Public sharing disabled');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || 'Failed to toggle public sharing';
      toast.error(errorMessage);
    } finally {
      setIsLoadingPublicShare(false);
    }
  };

  const handleRegeneratePublicLink = async () => {
    if (!window.confirm('This will invalidate the old link. Continue?')) return;

    setIsLoadingPublicShare(true);
    try {
      const response = await regeneratePublicLink(documentId);
      const { data } = response || {};
      setPublicToken(data.public_token);
      toast.success('New public link generated');
    } catch (error) {
      toast.error('Failed to regenerate link');
    } finally {
      setIsLoadingPublicShare(false);
    }
  };

  const handleCopyPublicLink = () => {
    const publicUrl = getPublicUrl(publicToken);
    copyToClipboard(publicUrl);
    setIsPublicLinkCopied(true);
    toast.success('Public link copied to clipboard!');
  };

  const canUsePublicSharing = () => {
    const planName = user?.subscriptions?.current_subscription?.plan_name;
    return planName === 'Business Plan' || planName === 'AppSumo Tier3';
  };

  const addToPendingInvites = ({ email, shareType }, setFieldValue) => {
    if (email === user.email) {
      toast.warning('You cannot share document with yourself');
      return;
    }

    if (email === documentOwner.email) {
      toast.warning('You cannot share document with document owner');
      return;
    }

    // Check if already in collaborators or pending
    const matchedCollaborator = collaborators.find(
      (item) => item.collaborator.email === email
    );

    const matchedPendingCollaborator = pendingCollaborators.find(
      (item) => item.email === email
    );

    const matchedPendingInvite = pendingInvites.find(
      (item) => item.email === email
    );

    if (matchedCollaborator || matchedPendingCollaborator || matchedPendingInvite) {
      toast.warning('This user is already added or has access');
      return;
    }

    // Find the access level details
    const accessLevel = options.find((option) => option.uuid === shareType);

    // Add to pending invites list
    setPendingInvites([...pendingInvites, { email, shareType, accessLevel }]);

    // Clear form
    setFieldValue('email', '');
    setFieldValue('shareType', '');
  };

  const removePendingInvite = (email) => {
    setPendingInvites(pendingInvites.filter((invite) => invite.email !== email));
  };

  const shareAllPendingInvites = async () => {
    if (pendingInvites.length === 0) {
      toast.warning('No users to share with');
      return;
    }

    const emails = pendingInvites.map((invite) => invite.email);
    const firstAccessLevel = pendingInvites[0].shareType;

    try {
      const addCollaboratorRequest = await postRequest(
        isDocument ? `shared_documents` : 'shared_folders',
        {
          [isDocument ? 'shared_document' : 'shared_folder']: {
            collaborators: emails,
            document_id: documentId,
            access_level_id: firstAccessLevel,
            folder_id: folder?.id,
          },
        },
      );

      if (addCollaboratorRequest.status === 200) {
        toast.success(`Successfully shared with ${pendingInvites.length} user(s)!`);
        setPendingInvites([]);
        fetchCollaborators();
        fetchFolders && fetchFolders();
      }
    } catch (error) {
      toast.error('Failed to share with users');
    }
  };

  const addCollaborator = async ({ email, shareType, note }, setFieldValue) => {
    if (email === user.email) {
      toast.warning('You cannot share document with yourself');
    } else if (email === documentOwner.email) {
      toast.warning('You cannot share document with document owner');
    } else {
      const matchedCollaborator = collaborators.find(
        (item) =>
          item.collaborator.email === email &&
          item.access_level.uuid === shareType,
      );

      const matchedPendingCollaborator = pendingCollaborators.find(
        (item) => item.email === email,
      );

      if (matchedCollaborator || matchedPendingCollaborator) {
        toast.warning(
          'Hold up! seems this collaborator is already in your list',
        );
      } else {
        const addCollaboratorRequest = await postRequest(
          isDocument ? `shared_documents` : 'shared_folders',
          {
            [isDocument ? 'shared_document' : 'shared_folder']: {
              collaborators: [email],
              document_id: documentId,
              access_level_id: shareType,
              folder_id: folder?.id,
              note,
            },
          },
        );
        if (addCollaboratorRequest.status === 200) {
          toast.success('Invite sent out successfully!');
          fetchCollaborators();
          fetchFolders && fetchFolders();
        }
        setFieldValue('email', '');
        setFieldValue('shareType', '');
      }
    }

    props.onHide && props.onHide();
  };

  return (
    <div>
      <Modal
        {...props}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        dialogClassName={ModalStyles.shareModal}
        style={{ zIndex: 10000 }}
      >
        <div className={ModalStyles.modalHeader}>
          <h2>Share "{document?.title || folder?.name || 'Untitled'}"</h2>
        </div>
        <Modal.Body className={ModalStyles.modalBody}>
          {/* Add People Section */}
          <div className={ModalStyles.addPeopleSection}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              validateOnMount={true}
              enableReinitialize={true}
            >
              {({ values, isValid, setFieldValue }) => {
                return (
                  <div className={ModalStyles.inputWrapper}>
                    <FiMail size={16} className={ModalStyles.mailIcon} />
                    <FormikTextField
                      name="email"
                      type="email"
                      placeholder="Add people by email"
                      classList="input"
                      inputWrapperClass={ModalStyles.inputWrapperClass}
                    />
                    <div className={ModalStyles.permissionSelect}>
                      <Select
                        isSearchable={false}
                        styles={Styles}
                        options={options}
                        placeholder="Role"
                        value={
                          options.find(
                            (option) => option.uuid === values.shareType,
                          ) || null
                        }
                        onChange={({ value, id, uuid }) => {
                          setFieldValue('shareType', uuid);
                        }}
                      />
                    </div>
                    <button
                      className={ModalStyles.sendButton}
                      disabled={!isValid}
                      onClick={() => addToPendingInvites(values, setFieldValue)}
                    >
                      Add
                    </button>
                  </div>
                );
              }}
            </Formik>
          </div>

          {/* Pending Invites Section */}
          {pendingInvites.length > 0 && (
            <div className={ModalStyles.pendingInvitesSection}>
              <div className={ModalStyles.pendingHeader}>
                <h3 className={ModalStyles.sectionTitle}>
                  Selected to share ({pendingInvites.length})
                </h3>
                <button
                  className={ModalStyles.shareAllButton}
                  onClick={shareAllPendingInvites}
                >
                  Share ({pendingInvites.length})
                </button>
              </div>
              <div className={ModalStyles.pendingList}>
                {pendingInvites.map((invite) => (
                  <div key={invite.email} className={ModalStyles.pendingInviteItem}>
                    <div className={ModalStyles.pendingInfo}>
                      <div className={ModalStyles.pendingAvatar}>
                        {invite.email.charAt(0).toUpperCase()}
                      </div>
                      <div className={ModalStyles.pendingDetails}>
                        <p className={ModalStyles.pendingEmail}>{invite.email}</p>
                        <p className={ModalStyles.pendingRole}>
                          {invite.accessLevel?.label?.props?.children[0]?.props?.children || 'Role'}
                        </p>
                      </div>
                    </div>
                    <button
                      className={ModalStyles.removeButton}
                      onClick={() => removePendingInvite(invite.email)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Users with Vocable Access Section */}
          <div className={ModalStyles.peopleWithAccessSection}>
            <h3 className={ModalStyles.sectionTitle}>Vocable Users Access</h3>
            <div className={ModalStyles.collaboratorsList}>
              <Collaborator
                profileImg={
                  documentOwner.id === user.id
                    ? user.medium_avatar
                    : documentOwner.medium_avatar
                }
                name={documentOwner.name}
                email={documentOwner.email}
                nameLetters={getNameInitials(documentOwner.name)}
                options={options}
                accessLevel={
                  options.find((option) => option.value === 'full_access')?.id
                }
                owner={true}
              />

              {collaborators.map((collaboratorInfo) => {
                const { collaborator, access_level, id } = collaboratorInfo || {};
                const {
                  name,
                  email,
                  medium_avatar,
                  id: collaboratorId,
                } = collaborator || {};
                const { id: accessLevel } = access_level || {};

                return (
                  <Collaborator
                    key={collaboratorId}
                    collaboratorId={collaboratorId}
                    profileImg={medium_avatar}
                    name={name}
                    email={email}
                    nameLetters={getNameInitials(name)}
                    options={options}
                    accessLevel={accessLevel}
                    sharedDocumentId={id}
                    reload={() => fetchCollaborators()}
                    isDocument={isDocument}
                  />
                );
              })}

              {pendingCollaborators.map((collaboratorInfo) => {
                const { email, access_level_id: accessLevel } =
                  collaboratorInfo || {};

                return (
                  <Collaborator
                    key={email}
                    name={email}
                    email={email}
                    options={options}
                    accessLevel={accessLevel}
                    pending={true}
                    nameLetters={getNameInitials(email)}
                  />
                );
              })}
            </div>
          </div>

          {/* General Access Section (Public Link) */}
          {isDocument && (
            <div className={ModalStyles.generalAccessSection}>
              {canUsePublicSharing() ? (
                <>
                  <div className={ModalStyles.sectionHeader}>
                    <h3 className={ModalStyles.sectionTitle}>General access</h3>
                  </div>

                  <div
                    className={ModalStyles.accessToggle}
                    onClick={() => !isLoadingPublicShare && !publicSharingEnabled && handlePublicSharingToggle(true)}
                  >
                    <div className={ModalStyles.toggleInfo}>
                      <div className={ModalStyles.icon}>
                        <FiLink />
                      </div>
                      <div className={ModalStyles.textInfo}>
                        <p className={ModalStyles.title}>
                          {publicSharingEnabled ? 'Anyone with the link' : 'Restricted'}
                        </p>
                        <p className={ModalStyles.description}>
                          {publicSharingEnabled
                            ? 'Anyone with the link can view'
                            : 'Only people with access can open'}
                        </p>
                      </div>
                    </div>
                    <div className={ModalStyles.toggleActions}>
                      <label
                        className={ModalStyles.toggleSwitch}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={publicSharingEnabled}
                          onChange={(e) => {
                            e.stopPropagation();
                            handlePublicSharingToggle(!publicSharingEnabled);
                          }}
                          disabled={isLoadingPublicShare}
                        />
                        <span className={ModalStyles.toggleSlider}></span>
                      </label>
                    </div>
                  </div>

                  {publicSharingEnabled && (
                    <div className={ModalStyles.linkDetails}>
                      <div className={ModalStyles.linkInput}>
                        <input
                          type="text"
                          value={publicToken ? getPublicUrl(publicToken) : 'Generating link...'}
                          readOnly
                        />
                        <button
                          className={`${ModalStyles.copyButton} ${isPublicLinkCopied ? ModalStyles.copied : ''}`}
                          onClick={handleCopyPublicLink}
                          disabled={!publicToken}
                        >
                          <FiCopy />
                          {isPublicLinkCopied ? 'Copied!' : 'Copy link'}
                        </button>
                      </div>
                      <div className={ModalStyles.linkActions}>
                        <button
                          className={ModalStyles.regenerateButton}
                          onClick={handleRegeneratePublicLink}
                          disabled={isLoadingPublicShare || !publicToken}
                        >
                          <FiRefreshCw />
                          Regenerate link
                        </button>
                        <button
                          className={ModalStyles.disableButton}
                          onClick={() => handlePublicSharingToggle(false)}
                          disabled={isLoadingPublicShare}
                        >
                          Disable link
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className={ModalStyles.upgradeBanner}>
                  <div className={ModalStyles.bannerIcon}>
                    <FiLink />
                  </div>
                  <div className={ModalStyles.bannerContent}>
                    <p className={ModalStyles.bannerTitle}>Public link sharing</p>
                    <p className={ModalStyles.bannerDescription}>
                      Upgrade to Business Plan to share documents publicly
                    </p>
                  </div>
                  <button
                    className={ModalStyles.upgradeButton}
                    onClick={() => navigate('/subscription')}
                  >
                    Upgrade
                  </button>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={ModalStyles.modalFooter}>
          <button
            className={ModalStyles.doneButton}
            onClick={props.onHide}
          >
            Done
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ShareModal;
